import axios from 'axios';

// Use relative URL for same-domain deployments (like Vercel)
// Use full URL only for cross-domain deployments
const API_URL = process.env.NODE_ENV === 'development' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');

// Create a single shared axios instance
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-store', // Always ask for fresh data
  }
});

// Add request interceptor for logging
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Utility to extract error message safely
function getErrorMessage(error) {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (error.message) return error.message;
  if (error.type && error.type === 'error' && error.target) return 'A network error occurred.';
  // Handle Event objects specifically
  if (error instanceof Event) return 'An unexpected event occurred.';
  if (typeof error.toString === 'function') return error.toString();
  return 'Unknown error';
}

// Add response interceptor for error handling and logging
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Log error response
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: getErrorMessage(error)
    });

    // Handle specific error cases
    if (error.response?.status === 401) {
      // Don't redirect for accounts endpoint - let the service handle fallback
      if (error.config?.url?.includes('/accounts') && !error.config?.url?.includes('/accounts/public')) {
        console.log('401 on accounts endpoint - allowing service to handle fallback');
        return Promise.reject(error);
      }
      // Don't redirect for logout endpoint - let the logout function handle navigation
      if (error.config?.url?.includes('/auth/logout')) {
        console.log('401 on logout endpoint - allowing logout function to handle navigation');
        return Promise.reject(error);
      }
      // Don't redirect for workflows endpoint - let the component handle the error
      if (error.config?.url?.includes('/custom-agents')) {
        console.log('401 on workflows endpoint - allowing component to handle error');
        return Promise.reject(error);
      }
      // Redirect to login for other 401 errors
      // Check if we're on a team auth page and redirect accordingly
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/team-')) {
        window.location.href = '/team-login';
      } else {
        window.location.href = '/login';
      }
    }

    // Optional: Intercept 304 and return cached data or empty object
    if (error.response && error.response.status === 304) {
      // Return empty data or handle as needed
      return Promise.resolve({ data: {} });
    }

    return Promise.reject(error);
  }
);

export default api; 